import React, {useState, useEffect, useRef, useCallback, useMemo} from "react";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import {courierStatusColors, receiverColor, pickupColor} from "../../../constants/constants";
import {
    configureLeafletMarkerIcons,
    addTileLayerWithFallback,
    getTileLayerPreference,
    TURKEY_BOUNDS,
    TILE_LAYER_TYPES
} from "../../../utils/helpers";
import {CourierIcon} from "../../_defaultComponents/CourierIcon/CourierIcon";
import {ShopOutlined, EnvironmentOutlined} from "@ant-design/icons";
import {useStore} from "hooks/useStore";
import {useTranslation} from "react-i18next";
import {SubcompanyMarker} from "components/_defaultComponents/SubcompanyMarker/SubcompanyMarker";
import {Content} from "../Marker/PickupMarker/Content";
import CourierPopup from "../../customer/customerHomePageWithShiftScore/components/CourierPopup";
import {useFetch} from "../../../hooks/useFetch";
import {ReceiverPopupContent} from "../Marker/ReceiverPopupContent";
import {
    CloseButton,
    ExternalPopup,
    PopupContent
} from "components/customer/customerHomePageWithShiftScore/style/CustomerHomeMapComponent.styles";

export const CourierLeafletMap = ({
    getCourierData,
    subCompanyMarkerList = [],
    regionData,
    regionGroupData,
    companyList,
    userSubCompanies,
    drawedPolygons,
    setDrawedPolygons,
    integrationType,
    isSubcompanyPoolSelected,
    mapInstanceRef: externalMapInstanceRef,
    tileLayerRef: externalTileLayerRef
}) => {
    const stores = useStore();
    const [selectedCourier, setSelectedCourier] = useState(null);
    const [selectedSubCompany, setSelectedSubCompany] = useState(null);
    const [selectedReceiver, setSelectedReceiver] = useState(null);
    const [popupPosition, setPopupPosition] = useState({x: 0, y: 0});
    const [userHasInteracted, setUserHasInteracted] = useState(false);
    const mapRef = useRef(null);
    const mapInstanceRef = externalMapInstanceRef || useRef(null);
    const markersRef = useRef([]);
    const popupRef = useRef(null);
    const tileLayerRef = externalTileLayerRef || useRef(null);

    const selectedCourierId = useMemo(() => {
        if (!selectedCourier) return null;
        return (
            selectedCourier?.courier?._id ||
            selectedCourier?.courierId ||
            selectedCourier?._id ||
            null
        );
    }, [selectedCourier]);

    const [courierInfoLoading, courierInfo, setCourierInfo] = useFetch({
        fetchFn: () =>
            selectedCourierId
                ? stores.courierStore.getCourierInfo({courierId: selectedCourierId})
                : null,
        dependencies: [selectedCourierId],
        condition: selectedCourierId,
        defaultDataValue: null,
        initialCall: false,
        waitForResponse: true
    });

    const clearSelections = useCallback(() => {
        setSelectedCourier(null);
        setSelectedSubCompany(null);
        setSelectedReceiver(null);
        setCourierInfo(null);
    }, [setCourierInfo]);

    // Helper function to get status color
    const getStatusColor = status => {
        return courierStatusColors[status] || "#d9d9d9";
    };

    // Leaflet map initialization
    useEffect(() => {
        const initializeMap = () => {
            if (mapRef.current && !mapInstanceRef.current) {
                // Configure Leaflet marker icons using helper function
                configureLeafletMarkerIcons(L);
                try {
                    // Türkiye sınırları
                    const turkeyBounds = L.latLngBounds(
                        TURKEY_BOUNDS.southWest,
                        TURKEY_BOUNDS.northEast
                    );

                    mapInstanceRef.current = L.map(mapRef.current, {
                        maxBounds: turkeyBounds,
                        maxBoundsViscosity: 1.0,
                        minZoom: 7,
                        maxZoom: 22,
                        zoomControl: false
                    }).setView(
                        [
                            stores.meStore.map.settings.center.lat,
                            stores.meStore.map.settings.center.lng
                        ],
                        stores.meStore.map.settings.zoom
                    );

                    tileLayerRef.current = addTileLayerWithFallback(
                        L,
                        mapInstanceRef.current,
                        TILE_LAYER_TYPES.BASIC
                    );

                    mapInstanceRef.current.on("zoomend", () => {
                        setUserHasInteracted(true);
                    });
                    mapInstanceRef.current.on("moveend", () => {
                        setUserHasInteracted(true);
                    });

                    const mapContainer = mapInstanceRef.current.getContainer();
                    if (mapContainer) {
                        const controls = mapContainer.querySelectorAll(".leaflet-control");
                        controls.forEach(control => {
                            control.style.zIndex = "1000";
                        });
                    }

                    setTimeout(() => {
                        if (mapInstanceRef.current) {
                            mapInstanceRef.current.invalidateSize();
                        }
                    }, 100);
                } catch (error) {
                    console.error("Map initialization error:", error);
                }
            }
        };

        const waitForMapRef = (retryCount = 0) => {
            if (mapRef.current) {
                initializeMap();
            } else if (retryCount < 20) {
                setTimeout(() => waitForMapRef(retryCount + 1), 200);
            }
        };

        const timer = setTimeout(() => waitForMapRef(), 100);

        return () => {
            clearTimeout(timer);
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []);

    // Update map center when settings change
    useEffect(() => {
        if (mapInstanceRef.current && !userHasInteracted) {
            mapInstanceRef.current.setView(
                [stores.meStore.map.settings.center.lat, stores.meStore.map.settings.center.lng],
                stores.meStore.map.settings.zoom
            );
        }
    }, [stores.meStore.map.settings.center, stores.meStore.map.settings.zoom, userHasInteracted]);

    // Close popup when clicking outside
    useEffect(() => {
        const handleClickOutside = event => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                clearSelections();
            }
        };

        if (selectedCourier || selectedSubCompany || selectedReceiver) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [selectedCourier, selectedSubCompany, selectedReceiver, clearSelections]);

    // Update markers when courier data changes
    useEffect(() => {
        if (mapInstanceRef.current) {
            // Clear existing markers
            markersRef.current.forEach(marker => {
                mapInstanceRef.current.removeLayer(marker);
            });
            markersRef.current = [];

            // getCourierData() already contains all the filtering logic from CourierMapTab
            const courierList = getCourierData();

            // Add courier markers
            if (courierList && courierList.length > 0) {
                courierList
                    ?.filter(courierState => {
                        if (!integrationType) return true;
                        const courierIntegrationType = courierState.integrationType;
                        return courierIntegrationType === integrationType;
                    })
                    ?.forEach((courierState, index) => {
                        if (courierState?.location?.coordinates) {
                            const courierStatus =
                                courierState?.deliveryState || courierState.status;
                            const statusColor = getStatusColor(
                                !courierState?.courier?.dedicatedCompanyId &&
                                    courierStatus === "available"
                                    ? "ready"
                                    : courierStatus
                            );

                            // Create custom icon with CourierIcon
                            const iconHtml = ReactDOMServer.renderToString(
                                <div
                                    style={{
                                        width: "32px",
                                        height: "32px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: statusColor,
                                        borderRadius: "50%",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                        border: "2px solid #fff"
                                    }}
                                >
                                    <CourierIcon courierType={courierState.type} />
                                </div>
                            );

                            const customIcon = L.divIcon({
                                html: iconHtml,
                                className: "custom-marker-icon",
                                iconSize: [32, 32],
                                iconAnchor: [16, 16],
                                popupAnchor: [0, -20]
                            });

                            const marker = L.marker(
                                [
                                    courierState.location.coordinates[1],
                                    courierState.location.coordinates[0]
                                ],
                                {icon: customIcon}
                            );

                            // Add click event to show popup outside map
                            marker.on("click", e => {
                                const containerPoint = mapInstanceRef.current.latLngToContainerPoint(
                                    e.latlng
                                );
                                setPopupPosition({
                                    x: containerPoint.x,
                                    y: containerPoint.y
                                });
                                clearSelections();
                                setSelectedCourier(courierState);
                            });

                            marker.addTo(mapInstanceRef.current);
                            markersRef.current.push(marker);
                        }
                    });
            }

            // Add pickup markers
            courierList?.forEach(courierState => {
                if (courierState.delivery && courierState.delivery.pickupInfos) {
                    courierState.delivery.pickupInfos
                        ?.filter(pickup => {
                            return (
                                stores.meStore.map.filters.subCompany === "" ||
                                pickup._id.toString() === stores.meStore.map.filters.subCompany
                            );
                        })
                        ?.forEach(pickup => {
                            // Create pickup marker
                            const pickupIconHtml = ReactDOMServer.renderToString(
                                <div
                                    style={{
                                        width: "32px",
                                        height: "32px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: pickupColor,
                                        borderRadius: "50%",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                        border: "2px solid #fff"
                                    }}
                                >
                                    <ShopOutlined style={{color: "white", fontSize: "14px"}} />
                                </div>
                            );

                            const pickupIcon = L.divIcon({
                                html: pickupIconHtml,
                                className: "custom-marker-icon",
                                iconSize: [32, 32],
                                iconAnchor: [16, 16],
                                popupAnchor: [0, -16]
                            });

                            const pickupMarker = L.marker(
                                [pickup.location.latitude, pickup.location.longitude],
                                {icon: pickupIcon}
                            );

                            // Add click event to show pickup popup
                            pickupMarker.on("click", e => {
                                const containerPoint = mapInstanceRef.current.latLngToContainerPoint(
                                    e.latlng
                                );
                                setPopupPosition({
                                    x: containerPoint.x,
                                    y: containerPoint.y
                                });
                                clearSelections();
                                setSelectedSubCompany(pickup);
                            });

                            pickupMarker.addTo(mapInstanceRef.current);
                            markersRef.current.push(pickupMarker);
                        });
                }
            });

            // Add subcompany markers - use userSubCompanies which already has the correct filtering logic
            if (
                userSubCompanies &&
                userSubCompanies.length > 0 &&
                stores.meStore.map.options.showSubCompanies
            ) {
                userSubCompanies
                    .filter(subcompany => isSubcompanyPoolSelected(subcompany))
                    .forEach(subcompany => {
                        // Create subcompany marker using SubcompanyMarker component
                        const subcompanyIconHtml = SubcompanyMarker({
                            subcompany,
                            isPoolCustomer: stores.meStore.isPoolCustomer
                        });

                        const subcompanyIcon = L.divIcon({
                            html: subcompanyIconHtml,
                            className: "custom-marker-icon",
                            iconSize: [32, 32],
                            iconAnchor: [16, 16],
                            popupAnchor: [0, -16]
                        });

                        const subcompanyMarker = L.marker(
                            [subcompany.location.latitude, subcompany.location.longitude],
                            {icon: subcompanyIcon}
                        );

                        // Add click event to show subcompany popup
                        subcompanyMarker.on("click", e => {
                            const containerPoint = mapInstanceRef.current.latLngToContainerPoint(
                                e.latlng
                            );
                            setPopupPosition({
                                x: containerPoint.x,
                                y: containerPoint.y
                            });
                            clearSelections();
                            setSelectedSubCompany(subcompany);
                        });

                        subcompanyMarker.addTo(mapInstanceRef.current);
                        markersRef.current.push(subcompanyMarker);
                    });
            }

            // Add receiver markers
            courierList
                ?.filter(courierState => {
                    if (stores.meStore.map.filters.subCompany === "") return true;
                    return courierState?.delivery?.pickupInfos.some(
                        pickupInfo =>
                            pickupInfo?._id?.toString() === stores.meStore.map.filters.subCompany
                    );
                })
                ?.forEach(courierState =>
                    courierState?.orderSteps
                        ?.filter(orderStep => !orderStep.timeAsMinutes)
                        ?.forEach((orderStep, i) => {
                            // Create receiver marker
                            const receiverIconHtml = ReactDOMServer.renderToString(
                                <div
                                    style={{
                                        width: "32px",
                                        height: "32px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: receiverColor,
                                        borderRadius: "50%",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                        border: "2px solid #fff"
                                    }}
                                >
                                    <EnvironmentOutlined
                                        style={{color: "white", fontSize: "12px"}}
                                    />
                                </div>
                            );

                            const receiverIcon = L.divIcon({
                                html: receiverIconHtml,
                                className: "custom-marker-icon",
                                iconSize: [24, 24],
                                iconAnchor: [12, 12],
                                popupAnchor: [0, -12]
                            });

                            const receiverMarker = L.marker(
                                [
                                    orderStep.stepInfo.receiverLocation.lat,
                                    orderStep.stepInfo.receiverLocation.lon
                                ],
                                {icon: receiverIcon}
                            );

                            // Add click event to show receiver popup with order details
                            receiverMarker.on("click", e => {
                                const containerPoint = mapInstanceRef.current.latLngToContainerPoint(
                                    e.latlng
                                );
                                setPopupPosition({
                                    x: containerPoint.x,
                                    y: containerPoint.y
                                });
                                clearSelections();
                                // Store receiver info with order step details for popup
                                setSelectedReceiver({
                                    delivery: courierState.delivery,
                                    allOrdersOfDelivery: courierState.orderSteps,
                                    order: orderStep,
                                    courierStatus: courierState.status
                                });
                            });

                            receiverMarker.addTo(mapInstanceRef.current);
                            markersRef.current.push(receiverMarker);
                        })
                );
        }
    }, [
        getCourierData,
        userSubCompanies,
        integrationType,
        stores.meStore.map.filters.subCompany,
        stores.meStore.map.filters.company,
        stores.meStore.map.filters.city,
        stores.meStore.map.options.showDummyCouriers,
        stores.meStore.map.options.showSubCompanies,
        clearSelections
    ]);

    // Handle region polygons
    useEffect(() => {
        if (mapInstanceRef.current && regionData) {
            // Clear existing polygons
            drawedPolygons.forEach(polygon => {
                mapInstanceRef.current.removeLayer(polygon);
            });

            if (stores.meStore.map.options.showRegions) {
                const newPolygons = [];
                regionData.forEach(region => {
                    if (
                        region.polygon &&
                        region.polygon.coordinates &&
                        region.polygon.coordinates[0]
                    ) {
                        const coordinates = region.polygon.coordinates[0].map(coord => [
                            coord[1],
                            coord[0]
                        ]);
                        const polygon = L.polygon(coordinates, {
                            color: "#888888",
                            weight: 2,
                            opacity: 1,
                            fillColor: "#ffffff",
                            fillOpacity: 0
                        });
                        polygon.addTo(mapInstanceRef.current);
                        newPolygons.push(polygon);
                    }
                });
                setDrawedPolygons(newPolygons);
            } else {
                setDrawedPolygons([]);
            }
        }
    }, [regionData, stores.meStore.map.options.showRegions]);

    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                minHeight: "170px",
                position: "relative",
                zIndex: 1
            }}
        >
            <div ref={mapRef} style={{width: "100%", height: "100%"}} />

            {/* External popup */}
            {selectedCourier && (
                <div
                    ref={popupRef}
                    style={{
                        position: "absolute",
                        left: popupPosition.x + 20,
                        top: popupPosition.y - 100,
                        zIndex: 10000,
                        backgroundColor: "white",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        minWidth: "280px",
                        maxWidth: "320px",
                        border: "1px solid #ddd",
                        padding: "12px"
                    }}
                >
                    <button
                        onClick={clearSelections}
                        style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            background: "none",
                            border: "none",
                            fontSize: "18px",
                            cursor: "pointer",
                            color: "#666"
                        }}
                    >
                        ×
                    </button>
                    <CourierPopup
                        courier={selectedCourier}
                        details={courierInfo}
                        isLoading={courierInfoLoading}
                    />
                </div>
            )}

            {/* SubCompany popup */}
            {selectedSubCompany && (
                <ExternalPopup ref={popupRef} x={popupPosition.x} y={popupPosition.y}>
                    <PopupContent>
                        <CloseButton onClick={() => setSelectedSubCompany(null)}>×</CloseButton>
                        <Content pickupInfo={selectedSubCompany} />
                    </PopupContent>
                </ExternalPopup>
            )}

            {/* Receiver popup */}
            {selectedReceiver && (
                <ExternalPopup ref={popupRef} x={popupPosition.x} y={popupPosition.y}>
                    <PopupContent>
                        <CloseButton onClick={() => setSelectedReceiver(null)}>×</CloseButton>
                        <ReceiverPopupContent
                            delivery={selectedReceiver.delivery}
                            allOrdersOfDelivery={selectedReceiver.allOrdersOfDelivery}
                            order={selectedReceiver.order}
                            courierStatus={selectedReceiver.courierStatus}
                        />
                    </PopupContent>
                </ExternalPopup>
            )}
        </div>
    );
};
